<template>
  <Dialog
    :visible="visible"
    @update:visible="$emit('update:visible', $event)"
    :header="$t('messages.markedtransactions.broker-dialog-title')"
    :style="{ width: '60rem', maxWidth: '95vw' }"
    :draggable="false"
    modal
  >
    <div v-if="selectedIds.length === 0" class="p-4 text-center text-gray-500">
      {{ $t('messages.markedtransactions.broker-dialog-empty') }}
    </div>

    <template v-else>
      <p class="mb-3 text-sm text-gray-600 dark:text-gray-300">
        {{ $t('messages.markedtransactions.broker-dialog-help') }}
      </p>

      <DataTable
        :value="previewRows"
        stripedRows
        showGridlines
        size="small"
        data-testid="broker-export-preview"
      >
        <Column field="address" :header="$t('messages.markedtransactions.broker-preview-address')" />
        <Column field="date" :header="$t('messages.markedtransactions.broker-preview-date')" style="width: 9rem;" />
        <Column field="price" :header="$t('messages.markedtransactions.broker-preview-price')" style="width: 10rem;">
          <template #body="slotProps">
            <span class="tabular-nums">{{ slotProps.data.price }}</span>
          </template>
        </Column>
        <Column field="area" :header="$t('messages.markedtransactions.broker-preview-area')" style="width: 7rem;">
          <template #body="slotProps">
            <span class="tabular-nums">{{ slotProps.data.area }}</span>
          </template>
        </Column>
        <Column style="width: 3rem;">
          <template #body="slotProps">
            <Button
              icon="pi pi-times"
              severity="danger"
              variant="text"
              size="small"
              rounded
              :aria-label="$t('messages.markedtransactions.broker-remove-row')"
              v-tooltip.left="$t('messages.markedtransactions.broker-remove-row')"
              :data-testid="`broker-remove-row-${slotProps.data.id}`"
              @click="brokerSelectionStore.remove(slotProps.data.id)"
            />
          </template>
        </Column>
      </DataTable>
    </template>

    <template #footer>
      <div class="flex justify-between w-full gap-2">
        <Button
          severity="danger"
          size="small"
          variant="text"
          :disabled="selectedIds.length === 0"
          @click="clearSelection"
        >
          {{ $t('messages.markedtransactions.clear') }}
        </Button>
        <div class="flex gap-2">
          <Button size="small" severity="secondary" @click="$emit('update:visible', false)">
            {{ $t('messages.close') }}
          </Button>
          <Button
            size="small"
            severity="primary"
            :disabled="selectedIds.length === 0 || exporting"
            :loading="exporting"
            data-testid="broker-export-trigger"
            @click="exportBrokerTable"
          >
            <span class="pi pi-file-word mr-1"></span>
            {{ $t('messages.markedtransactions.export-broker') }}
          </Button>
        </div>
      </div>
    </template>
  </Dialog>
</template>

<script setup>
import axios from 'axios'
import { storeToRefs } from 'pinia'
import Button from 'primevue/button'
import Column from 'primevue/column'
import DataTable from 'primevue/datatable'
import Dialog from 'primevue/dialog'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useHelpers } from '@/composables/useHelpers'
import { generalizeTransactionAddress } from '@/services/utilities'
import { useBrokerSelectionStore } from '@/stores/brokerSelectionStore'

// The exact field set Toomas's client specified for the broker .docx
// (RED-180 2026-05-27 Jira comment). Order matters — rows render top-to-
// bottom in the .docx.
const BROKER_FIELDS = [
  'aol_kpv',
  'tehingusumma',
  'm2hind',
  'korteri_pind',
  'hooneosa_pind',
  'hooneosa_tubade_arv',
  'korruste_arv',
  'kandekonstruktsioon',
  'ehitise_esmane_kasutus',
  'ehitise_seisukord',
  'hooneosa_seisukord',
  'hooneosa_valipindade_pind',
  'rodu',
  'koogimoobel',
  'kinnistu_parkimine',
  'tehnovorgud',
  'hooneosa_soojavarustuse_liik',
  'muu_info',
]

const props = defineProps({
  visible: { type: Boolean, default: false },
})

defineEmits(['update:visible'])

const { t } = useI18n()
const { composeData, calculateUnitPrice } = useHelpers()

const brokerSelectionStore = useBrokerSelectionStore()
const { selectedIds } = storeToRefs(brokerSelectionStore)

const exporting = ref(false)

// Cache of {detail, building, composedRow} per transaction id. Populated
// lazily from /api/transactions/{id}/details + /api/transactions/{id}/buildings
// directly (NOT via transactionStore) so the Hindamine `selectionTableRows`
// + `selectedBuilding` state is never touched.
const transactionData = ref({})

// IDs whose preview load failed (either /details or /buildings rejected).
// The exporter consults this set before POSTing so the user can't download
// a broker .docx with silently-empty rows for the failed transactions.
const loadFailures = ref(new Set())

const formatPrice = value => {
  if (value === null || value === undefined || value === '') return ''
  const num = Number(value)
  if (Number.isNaN(num)) return String(value)
  return `${num.toLocaleString('et-EE').replace(/,/g, ' ')} €`
}

const formatArea = value => {
  if (value === null || value === undefined || value === '') return ''
  const num = Number(value)
  if (Number.isNaN(num)) return String(value)
  return num.toLocaleString('et-EE', { maximumFractionDigits: 1 })
}

// Try to render a date robustly: parse the candidates in order, return
// the first that produces a valid Date; otherwise fall back to the raw
// string if we have one, or empty.
const formatDate = (...candidates) => {
  for (const candidate of candidates) {
    if (!candidate) continue
    const parsed = new Date(candidate)
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toLocaleDateString('et-EE')
    }
  }
  // None parseable — return the first non-empty raw value, if any.
  const raw = candidates.find(c => c != null && c !== '')
  return raw ? String(raw) : ''
}

const previewRows = computed(() =>
  selectedIds.value.map(id => {
    const entry = transactionData.value[id]
    const detail = entry?.detail
    if (!detail) {
      return { id, address: `#${id}`, date: '', price: '', area: '' }
    }
    // GDPR-safe address: mirror the server's generalisation so the preview
    // matches the rendered .docx. Falls back to `#<id>` if the transaction
    // has no usable address components — never to the precise `tais_aadress`.
    return {
      id,
      address: generalizeTransactionAddress(detail) ?? `#${id}`,
      date: formatDate(detail.aol_kpv, detail.kpv),
      price: formatPrice(detail.tehingusumma),
      area: formatArea(entry.composed?.korteri_pind || entry.composed?.hooneosa_pind),
    }
  }),
)

const loadPreviewFor = async id => {
  if (transactionData.value[id]) return true

  try {
    const [detailResp, buildingsResp] = await Promise.all([
      axios.get(`/api/transactions/${id}/details`),
      axios.get(`/api/transactions/${id}/buildings`),
    ])

    const detail = detailResp.data
    const buildings = Object.values(buildingsResp.data.buildings || {})
    const composed = composeData(detail, buildings[0] || null)

    transactionData.value = {
      ...transactionData.value,
      [id]: { detail, buildings, composed },
    }
    loadFailures.value.delete(id)
    return true
  } catch (err) {
    console.error(`Failed to load broker preview for transaction ${id}:`, err)
    loadFailures.value.add(id)
    return false
  }
}

watch(
  [() => props.visible, selectedIds],
  ([isVisible, ids]) => {
    if (!isVisible) return
    ids.forEach(id => loadPreviewFor(id))
  },
  { immediate: true, deep: true },
)

const clearSelection = () => {
  brokerSelectionStore.clear()
}

const buildExportRows = () => {
  // Each row is { key, label, "<txId>": value, ... } — same shape the
  // existing kohandustabel export uses, so DocxService can render it
  // without a new payload format.
  const composedPerTx = selectedIds.value.map(id => ({
    id,
    composed: transactionData.value[id]?.composed,
  }))

  return BROKER_FIELDS.map(field => {
    const row = { key: field }
    composedPerTx.forEach(({ id, composed }) => {
      if (!composed) {
        row[String(id)] = ''
        return
      }
      if (field === 'm2hind') {
        const m2 = calculateUnitPrice(composed, 'korteri_pind')
        row[String(id)] = m2 === '-' ? '' : m2
        return
      }
      row[String(id)] = composed[field] ?? ''
    })
    return row
  })
}

const exportBrokerTable = async () => {
  if (selectedIds.value.length === 0) return

  // Ensure all selected transactions have their composed data loaded
  // before we POST — if the user opens the dialog and clicks Export
  // very fast, some IDs may still be in flight.
  const loadResults = await Promise.all(
    selectedIds.value.map(id => loadPreviewFor(id)),
  )

  // Block the export if any selected transaction's preview failed to
  // load: otherwise the broker would download a .docx with silently-empty
  // cells for those rows. Better to surface the error than ship bad data.
  if (loadResults.some(ok => !ok)) {
    alert(t('messages.markedtransactions.export-broker-preview-load-failed'))
    return
  }

  const rows = buildExportRows()

  exporting.value = true
  try {
    const response = await axios.post(
      '/export/broker-table',
      {
        transaction_ids: selectedIds.value,
        rows,
      },
      { responseType: 'blob' },
    )

    const url = window.URL.createObjectURL(new Blob([response.data]))
    const link = document.createElement('a')
    link.href = url
    link.setAttribute(
      'download',
      `vordlevad_tehingud_${new Date().toISOString().split('T')[0]}.docx`,
    )
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
  } catch (error) {
    console.error('Broker table export failed:', error)
    alert(t('messages.markedtransactions.export-broker-failed'))
  } finally {
    exporting.value = false
  }
}
</script>
